import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';

import { useAskUiStoreApi } from '@/components/libraryChat/LibraryChatContext';
import {
  deleteAskConversation,
  patchAskConversation,
  type AskConversationPage,
  type AskConversationSnapshot,
  type AskRenamedConversation,
} from '@/services/ask/api';
import type { AskError } from '@/services/ask/errors';
import { askKeys, forgetAskConversation, refreshAskHistory } from '@/services/ask/queryOptions';
import {
  trackLibraryChatConversationDeleted,
  trackLibraryChatConversationRenamed,
} from '@/util/analyticsEvents';

type RenameVariables = { title: string; acknowledgedIdentifierWarning?: boolean };

type ListData = InfiniteData<AskConversationPage, string | undefined>;

function withTitle(data: ListData | undefined, conversationId: string, title: string | null) {
  return (
    data && {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        conversations: page.conversations.map((conversation) =>
          conversation.id === conversationId ? { ...conversation, title } : conversation
        ),
      })),
    }
  );
}

/**
 * Renames a conversation, showing the new title at once and putting the old one
 * back if the server refuses it. A `LIKELY_IDENTIFIER` refusal is returned to
 * the caller, which asks and resends with the acknowledgement.
 */
export function useRenameAskConversation(conversationId: string) {
  const queryClient = useQueryClient();

  return useMutation<
    AskRenamedConversation,
    AskError,
    RenameVariables,
    { previousTitle: string | null | undefined }
  >({
    mutationFn: ({ title, acknowledgedIdentifierWarning }) =>
      patchAskConversation({ conversationId, title, acknowledgedIdentifierWarning }),

    onMutate: async ({ title }) => {
      const key = askKeys.snapshot(conversationId);

      await queryClient.cancelQueries({ queryKey: key });

      const previousTitle = queryClient.getQueryData<AskConversationSnapshot>(key)?.title;

      queryClient.setQueryData<AskConversationSnapshot>(
        key,
        (snapshot) => snapshot && { ...snapshot, title }
      );

      queryClient.setQueriesData<ListData>({ queryKey: askKeys.lists }, (data) =>
        withTitle(data, conversationId, title)
      );

      return { previousTitle };
    },

    onError: (_error, _variables, context) => {
      const previousTitle = context?.previousTitle ?? null;

      queryClient.setQueryData<AskConversationSnapshot>(
        askKeys.snapshot(conversationId),
        (snapshot) => snapshot && { ...snapshot, title: previousTitle }
      );

      queryClient.setQueriesData<ListData>({ queryKey: askKeys.lists }, (data) =>
        withTitle(data, conversationId, previousTitle)
      );
    },

    onSuccess: () => trackLibraryChatConversationRenamed({ conversation_id: conversationId }),

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: askKeys.snapshot(conversationId) }).catch(() => {});

      refreshAskHistory(queryClient);
    },
  });
}

/**
 * Deletes a conversation. It is forgotten locally only once the server agrees.
 * It is marked as deleting here from the start, because the server can publish
 * its `conversation.deleted` hint before the response arrives, and that hint
 * must not tell this device's reader it was deleted somewhere else. A `404`
 * means it is already gone, which is the outcome asked for.
 */
export function useDeleteAskConversation(conversationId: string) {
  const queryClient = useQueryClient();

  const store = useAskUiStoreApi();

  return useMutation<void, AskError, void>({
    onMutate: () => store.getState().setDeletingHere(conversationId, true),

    onError: () => store.getState().setDeletingHere(conversationId, false),

    mutationFn: async () => {
      try {
        await deleteAskConversation(conversationId);
      } catch (error) {
        if ((error as AskError).kind !== 'NOT_FOUND') {
          throw error;
        }
      }
    },

    onSuccess: () => {
      forgetAskConversation(queryClient, conversationId);

      trackLibraryChatConversationDeleted({ conversation_id: conversationId });
    },
  });
}
